import React, { useState, useEffect, useRef, useCallback } from "react";
import supabase from "../utils/supabase";

interface Student {
  id: string;
  firstname: string;
  lastname: string;
  fullname: string;
}

interface StudentClassAutocompleteProps {
  value: string;
  onChange: (studentId: string) => void;
  selectedClassId?: string;
  placeholder?: string;
  className?: string;
  error?: string;
}

export const StudentClassAutocomplete: React.FC<
  StudentClassAutocompleteProps
> = ({
  value,
  onChange,
  selectedClassId,
  placeholder = "Select a student...",
  className = "",
  error,
}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 5;

  // Fetch students with pagination and search
  const fetchStudents = useCallback(
    async (search: string = "", page: number = 0, reset: boolean = false) => {
      setLoading(true);
      try {
        let query = supabase
          .from("profiles")
          .select("*", { count: "exact" })
          .order("firstname", { ascending: true });

        // Filter by class if selected
        if (selectedClassId && selectedClassId !== "all") {
          // Get students in the specific class
          const { data: classStudents } = await supabase
            .from("class_students")
            .select("user_id")
            .eq("class_id", selectedClassId);

          if (classStudents && classStudents.length > 0) {
            const studentIds = classStudents.map((cs) => cs.user_id);
            query = query.in("id", studentIds);
          } else {
            // No students in this class, return empty
            setStudents([]);
            setTotalCount(0);
            setHasMore(false);
            setLoading(false);
            return;
          }
        } else if (selectedClassId === "all") {
          // Get all students that are in any class
          const { data: allClassStudents } = await supabase
            .from("class_students")
            .select("user_id");

          if (allClassStudents && allClassStudents.length > 0) {
            const studentIds = allClassStudents.map((cs) => cs.user_id);
            query = query.in("id", studentIds);
          }
        }

        if (search.trim()) {
          query = query.or(
            `firstname.ilike.%${search.trim()}%,lastname.ilike.%${search.trim()}%`
          );
        }

        const { data, error, count } = await query.range(
          page * ITEMS_PER_PAGE,
          (page + 1) * ITEMS_PER_PAGE - 1
        );

        if (error) {
          console.error("Failed to fetch students:", error);
          return;
        }

        const newStudents = data || [];
        setTotalCount(count || 0);

        // Transform the data to include fullname
        const transformedStudents = newStudents.map((student: any) => ({
          ...student,
          fullname: `${student.firstname} ${student.lastname}`.trim(),
        }));

        if (reset) {
          setStudents(transformedStudents);
          setCurrentPage(0);
          // If we have a selectedStudentId, try to find the student in the new list
          if (selectedStudentId && transformedStudents.length > 0) {
            const student = transformedStudents.find(
              (s) => s.id === selectedStudentId
            );
            if (student) {
              setSelectedStudent(student);
            }
          }
        } else {
          setStudents((prev) => [...prev, ...transformedStudents]);
          setCurrentPage(page);
        }

        const hasMoreData = transformedStudents.length === ITEMS_PER_PAGE;
        setHasMore(hasMoreData);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    },
    [selectedClassId]
  );

  // Initial load
  useEffect(() => {
    fetchStudents("", 0, true);
  }, [fetchStudents]);

  // Search effect with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== "") {
        fetchStudents(searchTerm, 0, true);
      } else {
        setSearchLoading(true);
        fetchStudents("", 0, true);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchStudents]);

  // Filter students based on search term
  useEffect(() => {
    // Don't filter if we're currently searching
    if (searchLoading) {
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter((student) =>
        student.fullname.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  }, [students, searchTerm, searchLoading]);

  useEffect(() => {
    if (isOpen && hasMore && !loading && students.length > 0) {
      setShowScrollIndicator(true);
    }
  }, [isOpen, hasMore, loading, students.length]);

  // Set selected student when value changes
  useEffect(() => {
    if (value) {
      setSelectedStudentId(value);
      if (students.length > 0) {
        const student = students.find((s) => s.id === value);
        setSelectedStudent(student || null);
      }
    } else {
      setSelectedStudentId("");
      setSelectedStudent(null);
    }
  }, [value, students]);

  // Sync selectedStudent when students are loaded and we have a selectedStudentId
  useEffect(() => {
    if (selectedStudentId && students.length > 0) {
      const student = students.find((s) => s.id === selectedStudentId);
      if (student) {
        setSelectedStudent(student);
      }
    }
  }, [students, selectedStudentId]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      const nextPage = currentPage + 1;
      fetchStudents(searchTerm, nextPage, false);
    }
  };

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (student: Student) => {
    setSelectedStudent(student);
    onChange(student.id);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleInputClick = () => {
    if (!isOpen) {
      setIsOpen(true);
      setSearchTerm("");
      setHighlightedIndex(-1);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    // Set search loading immediately when typing starts
    if (value.trim() !== "") {
      setSearchLoading(true);
    }
    // Reset highlight when typing
    setHighlightedIndex(-1);
  };

  const clearSelection = () => {
    setSelectedStudent(null);
    onChange("");
    setSearchTerm("");
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
        setSearchTerm("");
        setHighlightedIndex(-1);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredStudents.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredStudents[highlightedIndex]) {
          handleSelect(filteredStudents[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
      case "Tab":
        setIsOpen(false);
        setSearchTerm("");
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : selectedStudent?.fullname || ""}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full px-3 py-1 text-black text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-black ${
            error
              ? "border-red-300"
              : isOpen
              ? "border-indigo-500"
              : "border-gray-300"
          } ${!isOpen ? "cursor-pointer" : ""}`}
          readOnly={!isOpen}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />

        {selectedStudent && !isOpen && (
          <button
            type="button"
            onClick={clearSelection}
            className="absolute right-8 top-1/2 transform -translate-y-1/2 text-black"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className={`w-4 h-4 transition-all duration-200 ${
              isOpen ? "rotate-180 text-indigo-500" : "text-black"
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {isOpen && (
        <div
          ref={optionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto animate-in fade-in-0 zoom-in-95 duration-200"
          role="listbox"
        >
          {searchLoading ? (
            <div className="px-3 py-1 text-sm text-gray-500 text-center">
              Loading...
            </div>
          ) : (searchLoading ? students : filteredStudents).length > 0 ? (
            <>
              {(searchLoading ? students : filteredStudents).map(
                (student, index) => (
                  <div
                    key={student.id}
                    onClick={() => handleSelect(student)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`px-3 py-1 text-sm cursor-pointer hover:bg-indigo-50 ${
                      selectedStudent?.id === student.id
                        ? "bg-indigo-100 text-indigo-900"
                        : "text-black"
                    } ${highlightedIndex === index ? "bg-indigo-50" : ""}`}
                    role="option"
                    aria-selected={selectedStudent?.id === student.id}
                  >
                    {student.fullname}
                  </div>
                )
              )}

              {showScrollIndicator && (
                <div className="px-1 py-1 border-t border-gray-200 bg-gray-50">
                  {loading ? (
                    <div className="flex items-center justify-center py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                      <span className="text-sm text-gray-500">
                        Loading more students...
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={handleLoadMore}
                      disabled={!hasMore}
                      className="w-full py-1 px-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Load More Students
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="px-3 py-1 text-sm text-gray-500 text-center">
              No students found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
